import {
  IPracticeTypeSuggestionRule,
  PracticeTypeRuleModality,
} from "types/PracticeTypeSuggestion/PracticeTypeSuggestion";
import {
  IProcedureModalityClassification,
  IProcedureModalityRange,
  IProcedureModalityRangeSaveRequest,
} from "types/ProcedureModalityRange/ProcedureModalityRange";

import { MODALITY_LABELS, serviceTypeLabel } from "./serviceTypeRuleWording";

/**
 * CLE-3000 — the words and checks of Eligibility › CPT Groups, in one place so the summary, the
 * table, the dialog and the tester say the same thing. The dialog's checks are the API's 400s, in
 * the API's words, made before anything is sent.
 */

/** Fixed in code on the API (`PROCEDURE_MODALITY_PRECEDENCE`): the first group holding a code wins. */
export const CPT_GROUP_PRECEDENCE: PracticeTypeRuleModality[] = [
  "MRI",
  "CT",
  "ULTRASOUND",
  "NUCLEAR_MEDICINE",
  "SLEEP_STUDY",
  "OTHER_DIAGNOSTIC_TEST",
  "XRAY",
];

/**
 * CLE-2992 — a list of CPT groups in words, in the fixed order: "Ultrasound · Nuclear medicine".
 * Used wherever a rule's CPT-group scope is shown (the line rules on the hospital page).
 */
export const cptGroupsText = (
  groups: PracticeTypeRuleModality[] | undefined
): string =>
  CPT_GROUP_PRECEDENCE.filter((group) => (groups ?? []).includes(group))
    .map((group) => MODALITY_LABELS[group])
    .join(" · ");

/** CLE-2992 — the sentence for a line rule's X-ray instruction, the same on both editors. */
export const COPAY_FROM_XRAY_LINE_LABEL =
  "Read the copay from the Diagnostic X-Ray facility line";
export const COPAY_FROM_XRAY_LINE_HELP =
  "Only on a check run under X-Ray + MRI/CAT Scan whose estimate has no MRI or CT code — MRIs and CTs always keep the MRI/CAT Scan block. For plans that answer ultrasounds only on the X-ray line (BCBS Preferred Care).";

export const PRECEDENCE_SENTENCE = `When a code is in more than one group, the first of ${CPT_GROUP_PRECEDENCE.map(
  (modality) => MODALITY_LABELS[modality]
).join(", ")} wins.`;

export const CPT_GROUPS_INTRO =
  "The service line map: the CPT and HCPCS code ranges behind each procedure type the Service Type rules key on. Every hospital uses the same groups. The nuclear medicine, sleep study and other diagnostic test ranges are a starting set — add the codes you need. The order between groups is fixed and cannot be changed here, because the radiology codes nest (70336 TMJ MRI sits inside the head X-ray range).";

export const NOTE_MAX_LENGTH = 200;

/** Five digits, or a HCPCS letter and four digits — the API's `PROCEDURE_CODE_SHAPE`. */
const CODE_SHAPE = /^(?:\d{5}|[A-Z]\d{4})$/;
const CODE_SHAPE_WORDS =
  "must be 5 characters: five digits, or a HCPCS letter and four digits.";

export const normalizeCode = (code: string): string =>
  (code ?? "").trim().toUpperCase();

const letterOf = (code: string): string | null =>
  /^[A-Z]/.test(code) ? code[0] : null;

export interface IRangeDraft {
  modality: PracticeTypeRuleModality | null;
  fromCode: string;
  /** Blank = one code (the same as `fromCode`). */
  toCode: string;
  note: string;
  enabled: boolean;
}

export const toRangeDraft = (
  range: IProcedureModalityRange | null,
  defaultModality: PracticeTypeRuleModality | null
): IRangeDraft => ({
  modality: range?.modality ?? defaultModality,
  fromCode: range?.fromCode ?? "",
  toCode: range && range.toCode !== range.fromCode ? range.toCode : "",
  note: range?.note ?? "",
  enabled: range?.enabled ?? true,
});

const codesOf = (draft: IRangeDraft) => {
  const fromCode = normalizeCode(draft.fromCode);
  const toCode = normalizeCode(draft.toCode) || fromCode;
  return { fromCode, toCode };
};

/** What is wrong with the draft, in words, or null. `editingId` never duplicates itself. */
export const rangeDraftProblem = (
  draft: IRangeDraft,
  existing: IProcedureModalityRange[],
  editingId: string | null
): string | null => {
  if (!draft.modality) return "Choose the group.";
  const { fromCode, toCode } = codesOf(draft);
  if (!CODE_SHAPE.test(fromCode)) return `The first code ${CODE_SHAPE_WORDS}`;
  if (!CODE_SHAPE.test(toCode)) return `The last code ${CODE_SHAPE_WORDS}`;
  if (letterOf(fromCode) !== letterOf(toCode)) {
    return `${fromCode} and ${toCode} are not the same kind of code: both five digits, or both the same HCPCS letter.`;
  }
  if (toCode < fromCode)
    return `The last code (${toCode}) comes before the first (${fromCode}).`;
  if (draft.note.trim().length > NOTE_MAX_LENGTH) {
    return `Keep the note under ${NOTE_MAX_LENGTH} characters.`;
  }
  const duplicate = existing.some(
    (range) =>
      range.id !== editingId &&
      range.modality === draft.modality &&
      range.fromCode === fromCode &&
      range.toCode === toCode
  );
  if (duplicate)
    return `This group already has the range ${fromCode}–${toCode}.`;
  return null;
};

export const rangeToSaveRequest = (
  draft: IRangeDraft
): IProcedureModalityRangeSaveRequest => ({
  modality: draft.modality as PracticeTypeRuleModality,
  ...codesOf(draft),
  note: draft.note.trim() || null,
  enabled: draft.enabled,
});

/** "70540–70559", or "70336" for one code. */
export const codesInWords = (fromCode: string, toCode: string): string =>
  fromCode === toCode ? fromCode : `${fromCode}–${toCode}`;

export interface IServiceLineSummaryRow {
  modality: PracticeTypeRuleModality;
  label: string;
  /** Enabled ranges. */
  rangeCount: number;
  disabledRangeCount: number;
  /** The Service Types the ENABLED all-hospitals rules for this group run under, as labels. */
  allHospitalsTargets: string[];
  /** ENABLED rules of one hospital for this group. */
  hospitalRuleCount: number;
}

/** The service line map: one row per group, in precedence order. Read-only. */
export const serviceLineSummary = (
  ranges: IProcedureModalityRange[],
  rules: IPracticeTypeSuggestionRule[]
): IServiceLineSummaryRow[] =>
  CPT_GROUP_PRECEDENCE.map((modality) => {
    const own = ranges.filter((range) => range.modality === modality);
    const enabledRules = rules.filter(
      (rule) => rule.modality === modality && rule.enabled
    );
    return {
      modality,
      label: MODALITY_LABELS[modality],
      rangeCount: own.filter((range) => range.enabled).length,
      disabledRangeCount: own.filter((range) => !range.enabled).length,
      allHospitalsTargets: enabledRules
        .filter((rule) => rule.accountId === null)
        .map((rule) => serviceTypeLabel(rule.targetPracticeTypeCode)),
      hospitalRuleCount: enabledRules.filter((rule) => rule.accountId !== null)
        .length,
    };
  });

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

/** "MRI — 12 ranges — 1 all-hospitals rule → MRI/CAT Scan (31); 1 hospital rule". */
export const summaryInWords = (line: IServiceLineSummaryRow): string => {
  const ranges =
    line.rangeCount === 0 && line.disabledRangeCount === 0
      ? "no ranges"
      : `${plural(line.rangeCount, "range", "ranges")}${
          line.disabledRangeCount > 0 ? ` (${line.disabledRangeCount} off)` : ""
        }`;
  const rules: string[] = [];
  if (line.allHospitalsTargets.length > 0) {
    rules.push(
      `${plural(
        line.allHospitalsTargets.length,
        "all-hospitals rule",
        "all-hospitals rules"
      )} → ${[...new Set(line.allHospitalsTargets)].join(", ")}`
    );
  }
  if (line.hospitalRuleCount > 0) {
    rules.push(
      plural(line.hospitalRuleCount, "hospital rule", "hospital rules")
    );
  }
  return `${line.label} — ${ranges} — ${
    rules.length > 0 ? rules.join("; ") : "no rule"
  }`;
};

/** The tester's answer for one code. */
export const classificationInWords = (
  result: IProcedureModalityClassification
): string => {
  if (result.modality === "OTHER" || !result.fromCode || !result.toCode)
    return "Not in any group";
  return `${MODALITY_LABELS[result.modality]} (${codesInWords(
    result.fromCode,
    result.toCode
  )})`;
};
